import React from "react";
import { FiTrendingUp, FiBarChart2, FiCpu, FiClock, FiServer, FiActivity, FiAlertTriangle, FiZap, FiCloud } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuZap,
  LuClipboardList,
  LuCode,
  LuBug,
  LuRocket,
  LuActivity,
  LuBarChart3,
  LuCircleCheck,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const performanceTestingHeroData = {
  backgroundImage: "/images/serviceBanners/PerformanceTesting.webp",
  whiteImage: "/images/serviceBanners/PerformanceTestingW.webp",
  subTitle: "We Create Performance Testing Solutions",
  title: (
    <>
      Optimize Reliability with <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Performance & Load Testing Solutions</span>
    </>
  ),
  description:
    "Performance and load testing solutions designed to identify bottlenecks, improve application speed, and ensure system stability under real world traffic conditions for reliable, scalable, and high performing digital platforms.",
  primaryButton: {
    title: "Get Performance Audit",
    onClick: () => console.log("Performance Start"),
  },
  secondaryButton: {
    title: "View Scalability Case",
    onClick: () => console.log("Performance Work"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Fast and optimized performance testing execution.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Load Testing",
      desc: "Validate system behavior under heavy user traffic.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Stress Testing",
      desc: "Identify system breaking points under extreme load.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Scalability Testing",
      desc: "Ensure applications scale smoothly with growing demand.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};

// 2. Stats Block
export const performanceStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const performanceSolutionsAndProcessData = {
  buildSubtitle: "OUR PERFORMANCE CAPABILITIES",
  buildTitle: "Performance Testing Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform Performance Testing",
      desc: "Test performance across Android and iOS using scalable environments.",
      icon: FiActivity,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise Performance Testing",
      desc: "Ensure stability and speed for large-scale enterprise applications.",
      icon: FiCloud,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Performance Testing",
      desc: "Validate speed, checkout flow, and heavy traffic handling capacities.",
      icon: FiBarChart2,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand Performance Testing",
      desc: "Test real-time booking and delivery systems under load conditions.",
      icon: FiTrendingUp,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Performance Testing",
      desc: "Ensure secure and high-speed financial transaction processing execution.",
      icon: FiCloud,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom Performance Testing",
      desc: "Tailored testing strategies based on specific application architecture requirements.",
      icon: FiTrendingUp,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "PERFORMANCE TESTING PROCESS",
  processTitle: "Performance Testing Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand application goals, user load expectations, and performance requirements.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define test strategy, tools, and load testing scenarios.",
      icon: LuSettings,
    },
    {
      step: "03",
      title: "Test Design",
      description: "Create performance test cases for load, stress, and scalability thresholds.",
      icon: LuActivity,
    },
    {
      step: "04",
      title: "Test Execution",
      description: "Run automated performance tests under different traffic conditions.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "Analysis & Reporting",
      description: "Identify bottlenecks and generate detailed performance profiles.",
      icon: LuCircleCheck, // Swapped with compile-safe Lucide icon variant for fast-tracking updates
    },
    {
      step: "06",
      title: "Optimization & Deployment",
      description: "Apply fixes and validate system stability before production release configurations.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR PERFORMANCE TESTING SERVICES?",
  benefitsTitle: "Why Businesses Choose Performance Testing Solutions",
  benefits: [
    "Cross-platform testing",
    "Faster test cycles",
    "Modern UI validation",
    "Smooth performance checks",
    "Efficient workflow system",
    "Scalable load testing",
    "Cost-effective QA testing",
  ],
  logoImage: "/images/serviceBanners/PerformanceTesting.webp",
  whiteLogoImage: "/images/serviceBanners/PerformanceTestingW.webp",
};
// 4. Portfolio Data
export const performancePortfolioData = {
  subtitle: "SUCCESS STORIES",
  title: "Systems That Never Break",
  buttonText: "View Case Studies",
  buttonLink: "#",
  apps: [
    {
      title: "Global E-Com Spike",
      badge: "Load Testing",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Simulated 500k concurrent users for a major retail launch with 100% platform stability.",
      downloads: "Active",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=800&q=80",
    },
  ],
};

// 5. Technologies Data
export const performanceTechnologiesData = {
  techTitle: "OUR PERFORMANCE STACK",
  technologies: [
    { name: "Apache JMeter", icon: "RestAPI" },
    { name: "Locust", icon: "Vite" },
    { name: "Gatling", icon: "ReactNative" },
    { name: "Datadog", icon: "Firebase" },
    { name: "Prometheus", icon: "Code" },
    { name: "New Relic", icon: "Provider" },
  ],
  trustTitle: "TRUSTED BY HIGH-TRAFFIC BRANDS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

// 6. About Overview
export const performanceAboutData = [
  {
    title: "Performance Testing Services for Reliable Digital Performance",
    content: (
      <>
        Modern applications must perform consistently even during high traffic and heavy user activity. Reliable{" "}
        <b className="text-text-primary font-semibold">performance testing services</b> help businesses identify system bottlenecks, improve responsiveness, and ensure digital
        products remain stable under real-world conditions. Many businesses struggle with slow loading systems and unexpected downtime that impact customer trust and operational
        efficiency. At <b className="text-text-primary font-semibold">Tycho Technologies</b>, performance strategies are developed around business goals, scalability needs, and
        technical reliability to help organizations maintain strong digital experiences.
      </>
    ),
  },
  {
    title: "Load Testing Company for Better System Stability",
    content: (
      <>
        A successful digital platform depends on how efficiently it performs during traffic spikes and peak activity. A trusted{" "}
        <b className="text-text-primary font-semibold">load testing company</b> helps businesses evaluate system behavior, improve infrastructure readiness, and maintain stable
        application performance under increasing demand. Companies investing in <b className="text-text-primary font-semibold">application performance testing</b> often improve
        user satisfaction because applications remain responsive during high-traffic scenarios. Tycho Technologies focuses on practical testing frameworks designed to improve
        software stability while supporting long-term business continuity.
      </>
    ),
  },
  {
    title: "Website Speed Optimization Services for Better User Experience",
    content: (
      <>
        Businesses aiming to improve customer engagement often prioritize digital experiences that remain fast and responsive across devices. Reliable{" "}
        <b className="text-text-primary font-semibold">website speed optimization services</b> help organizations reduce delays, improve usability, and strengthen conversion
        opportunities through smoother interactions. Companies looking to improve software reliability frequently combine performance validation with{" "}
        <a href="https://tychotechnologies.com/security-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          security testing services
        </a>{" "}
        to strengthen digital protection while maintaining stable system performance.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Performance Testing Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">performance testing company in india</b> continues to grow as businesses seek scalable quality
        assurance frameworks for modern applications. Companies investing in <b className="text-text-primary font-semibold">software performance testing india</b> often prioritize
        speed, operational reliability, and stronger digital experiences for customers. Businesses partnering with Tycho Technologies gain access to customized testing strategies
        designed around measurable business objectives and technical requirements. Many organizations also strengthen software stability through{" "}
        <a href="https://tychotechnologies.com/automated-regression-testing-india" className="text-cyan-400 underline hover:text-cyan-300">
          automated regression testing services
        </a>{" "}
        to maintain consistent product performance and long-term reliability.
      </>
    ),
  },
];

// 7. FAQs (Preserved)
export const performanceTestingFaqs = [
  {
    q: "Why is performance testing important?",
    a: "It ensures your application can handle peak loads, providing a smooth user experience and preventing downtime during critical business events.",
  },
  {
    q: "What tools do you use?",
    a: "We use industry-leading tools like JMeter, Gatling, and Locust, tailored to your technology stack and requirements.",
  },
];
